// apps/web/src/features/clients/components/ClientVersionRestoreModal.tsx
//
// Preview one historical version and, deliberately, restore it.
//
// INVARIANT (AC-11): restoring is a two-step act — read the snapshot and its
// impact, THEN confirm a prompt that names the client and the version. A single
// click can never rewrite the aggregate.
//
// INVARIANT (AC-11): a failed restore keeps the preview open and recoverable,
// and an ambiguous restore (`restore-ambiguous`) is a REFUSAL: the snapshot
// cannot prove which bank rows it belonged to, so there is no partial fallback
// to offer. The operator is told which legal entities to check.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientVersionListItem } from '@magimp/shared';
import { useClient, useClientVersion, useRestoreClientVersion } from '@magimp/shared-hooks';
import {
  Banner,
  Button,
  ConfirmModal,
  ErrorState,
  LoadingState,
  Modal,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { clientRestoreImpact } from '../clientVersionImpact.js';
import { clientConflictMessageKey, parseClientConflict } from '../clientDomainErrors.js';
import { useTouchActionStyle, useTouchControlHeightScopeStyle } from '../useMobileFieldStyle.js';
import { ClientRestoreImpactView } from './ClientRestoreImpactView.js';
import { ClientVersionSnapshotView } from './ClientVersionSnapshotView.js';

export interface ClientVersionRestoreModalProps {
  clientId: string;
  clientName: string;
  version: ClientVersionListItem;
  /** False for read-only actors and while the live aggregate is archived. */
  canManage: boolean;
  onClose: () => void;
}

export function ClientVersionRestoreModal({
  clientId,
  clientName,
  version,
  canManage,
  onClose,
}: ClientVersionRestoreModalProps) {
  const { t, i18n } = useTranslation(['clients', 'common']);
  const show = useToastStore((s) => s.show);
  const actionStyle = useTouchActionStyle();
  const errorScopeStyle = useTouchControlHeightScopeStyle();
  const detailQ = useClientVersion(clientId, version.id);
  const clientQ = useClient(clientId);
  const restore = useRestoreClientVersion(clientId, version.id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);

  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });
  const impact =
    detailQ.isSuccess && clientQ.isSuccess
      ? clientRestoreImpact(clientQ.data, detailQ.data.snapshot)
      : null;

  const onConfirmRestore = () => {
    // A second confirm click while the first is in flight would restore twice.
    if (restore.isPending) return;
    setFailure(null);
    restore.mutate(undefined, {
      onSuccess: () => {
        show({ tone: 'success', text: t('clients:toasts.versionRestored') });
        setConfirmOpen(false);
        onClose();
      },
      onError: (err) => {
        const conflict = parseClientConflict(err);
        setConfirmOpen(false);
        if (conflict?.kind === 'restore-ambiguous') {
          // No partial write happened and none will be offered: a guessed bank
          // row is worse than a refused restore.
          setRefused(true);
          setFailure(
            t('clients:conflict.restoreAmbiguousDetail', { count: conflict.legalEntityIds.length }),
          );
          return;
        }
        setFailure(conflict ? t(clientConflictMessageKey(conflict)) : errorMessage(err));
      },
    });
  };

  // While the restore is in flight, dismissing would leave the operator unsure
  // whether it landed — so every close path is held until it settles.
  const closeIfIdle = () => {
    if (restore.isPending) return;
    onClose();
  };

  return (
    <Modal
      open={true}
      title={t('clients:modals.versionTitle', { number: version.versionNumber })}
      width={560}
      narrowFullWidth
      onClose={closeIfIdle}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)', minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}>
          {fmt.format(new Date(version.createdAt))}
          {version.actor ? ` · ${version.actor.name}` : ''}
        </p>

        {failure !== null && (
          <div data-testid="client-version-restore-error">
            <Banner tone="error">{failure}</Banner>
          </div>
        )}

        {restore.isPending && (
          <div data-testid="client-version-restore-pending">
            <Banner tone="info">{t('clients:restore.pending')}</Banner>
          </div>
        )}

        {detailQ.isPending && <LoadingState />}
        {detailQ.error && (
          <div style={errorScopeStyle}>
            <ErrorState
              title={t('common:states.loadError')}
              text={errorMessage(detailQ.error)}
              onRetry={() => void detailQ.refetch()}
            />
          </div>
        )}
        {detailQ.isSuccess && (
          <>
            {impact ? (
              <ClientRestoreImpactView impact={impact} />
            ) : (
              // Without the live aggregate the effect cannot be described, and
              // silence would read as "nothing changes".
              !clientQ.isPending && (
                <Banner tone="warn">{t('clients:restore.impactUnavailable')}</Banner>
              )
            )}
            <ClientVersionSnapshotView snapshot={detailQ.data.snapshot} />
          </>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--s-3)',
          justifyContent: 'flex-end',
          marginTop: 'var(--s-4)',
        }}
      >
        <Button kind="ghost" disabled={restore.isPending} style={actionStyle} onClick={closeIfIdle}>
          {t('common:actions.close')}
        </Button>
        {canManage && !refused && (
          <Button
            kind="primary"
            disabled={!detailQ.isSuccess || restore.isPending}
            style={actionStyle}
            onClick={() => setConfirmOpen(true)}
            data-testid="client-version-restore"
          >
            {t('clients:actions.restoreVersion')}
          </Button>
        )}
      </div>

      {confirmOpen && (
        <ConfirmModal
          danger
          narrowFullWidth
          title={t('clients:confirm.restoreVersionTitle')}
          text={t('clients:confirm.restoreVersionMessage', {
            name: clientName,
            number: version.versionNumber,
          })}
          confirmLabel={t('clients:actions.restoreVersion')}
          onConfirm={onConfirmRestore}
          onClose={() => {
            if (!restore.isPending) setConfirmOpen(false);
          }}
        />
      )}
    </Modal>
  );
}
